import React, { useMemo, type ReactElement } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { markdownModeMessages as i18n } from '@atlaskit/editor-common/messages';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline } from '@atlaskit/primitives/compiled';

const TEST_ID = 'unlink-synced-blocks-dialog';

const styles = cssMap({
	footer: {
		width: '100%',
	},
});

export type UnlinkSyncedBlocksDialogBaseProps = {
	/** Whether the modal is currently visible. */
	isOpen: boolean;
	/**
	 * Destination of the "Learn more" link. Provided by the consumer
	 * because the canonical docs URL is product-specific.
	 */
	learnMoreHref: string;
	/** Called when the user dismisses (close icon, escape, blanket, Cancel, Understood). */
	onClose: () => void;
	/**
	 * Number of synced-block nodes detected in the document. Drives the
	 * pluralised body copy. Always `>= 1` in practice; `0` falls through
	 * to the plural branch defensively.
	 */
	syncBlockCount: number;
};

const UnlinkSyncedBlocksDialogContents = ({
	syncBlockCount,
	learnMoreHref,
	onClose,
}: Omit<UnlinkSyncedBlocksDialogBaseProps, 'isOpen'>) => {
	const intl = useIntl();
	const bodyValues = useMemo(() => ({ count: syncBlockCount }), [syncBlockCount]);
	const title = intl.formatMessage(i18n.unlinkSyncedBlocksTitle);
	const body = intl.formatMessage(i18n.unlinkSyncedBlocksBody, bodyValues);
	const learnMoreLabel = intl.formatMessage(i18n.unlinkSyncedBlocksLearnMore);
	const cancelLabel = intl.formatMessage(i18n.confirmDialogCancel);
	const understoodLabel = intl.formatMessage(i18n.unlinkSyncedBlocksUnderstood);

	return (
		<>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					<Inline as="span" space="space.100" alignBlock="center">
						{title}
						<Lozenge appearance="new">{intl.formatMessage(i18n.labsLozenge)}</Lozenge>
					</Inline>
				</ModalTitle>
			</ModalHeader>
			<ModalBody>{body}</ModalBody>
			<ModalFooter>
				<Box xcss={styles.footer}>
					<Inline alignBlock="center" spread="space-between">
						<Link
							href={learnMoreHref}
							target="_blank"
							rel="noopener noreferrer"
							testId={`${TEST_ID}--learn-more`}
						>
							{learnMoreLabel}
						</Link>
						<Inline space="space.100">
							<Button appearance="subtle" onClick={onClose} testId={`${TEST_ID}--cancel`}>
								{cancelLabel}
							</Button>
							<Button
								autoFocus={true}
								appearance="warning"
								onClick={onClose}
								testId={`${TEST_ID}--understood`}
							>
								{understoodLabel}
							</Button>
						</Inline>
					</Inline>
				</Box>
			</ModalFooter>
		</>
	);
};

/**
 * Presentational blocker dialog shown when the user tries to convert a
 * page to markdown but the document contains one or more `bodiedSyncBlock`
 * nodes.
 *
 * Synced blocks have no markdown representation and they fan out to other
 * pages, so there is no "convert anyway" action — the user must unlink
 * them in the editor first. Outcomes here: dismiss (Close / Cancel /
 * Understood) or "Learn more" (external docs).
 *
 * Stateless w.r.t. open/close: the consumer owns the dialog state and
 * the sync-block detection. This keeps the component reusable across
 * products that don't share the same state model.
 */
export const UnlinkSyncedBlocksDialogBase = ({
	isOpen,
	syncBlockCount,
	learnMoreHref,
	onClose,
}: UnlinkSyncedBlocksDialogBaseProps): ReactElement => (
	<ModalTransition>
		{isOpen ? (
			<Modal onClose={onClose} width="medium" testId={TEST_ID}>
				<UnlinkSyncedBlocksDialogContents
					syncBlockCount={syncBlockCount}
					learnMoreHref={learnMoreHref}
					onClose={onClose}
				/>
			</Modal>
		) : null}
	</ModalTransition>
);
